import { format, isValid, parse } from "date-fns";
import { CalendarIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DateRangePickerProps {
	id?: string;
	startDate: string;
	endDate: string;
	onStartChange: (value: string) => void;
	onEndChange: (value: string) => void;
	disabled?: boolean;
	className?: string;
}

function parseDate(value: string): Date | undefined {
	if (!value) return undefined;
	const d = parse(value, "yyyy-MM-dd", new Date());
	return isValid(d) ? d : undefined;
}

function formatDisplay(d: Date): string {
	return format(d, "MMM d, yyyy");
}

export function DateRangePicker({
	id,
	startDate,
	endDate,
	onStartChange,
	onEndChange,
	disabled = false,
	className,
}: DateRangePickerProps) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const from = parseDate(startDate);
	const to = parseDate(endDate);

	const [range, setRange] = useState<DateRange | undefined>(
		from || to ? { from, to } : undefined,
	);

	useEffect(() => {
		const f = parseDate(startDate);
		const t = parseDate(endDate);
		setRange(f || t ? { from: f, to: t } : undefined);
	}, [startDate, endDate]);

	function handleSelect(newRange: DateRange | undefined) {
		setRange(newRange);
		if (newRange?.from) {
			onStartChange(format(newRange.from, "yyyy-MM-dd"));
		} else {
			onStartChange("");
		}
		if (newRange?.to) {
			onEndChange(format(newRange.to, "yyyy-MM-dd"));
		} else {
			onEndChange("");
		}
		if (newRange?.from && newRange?.to) {
			setOpen(false);
		}
	}

	function handleClear() {
		setRange(undefined);
		onStartChange("");
		onEndChange("");
	}

	function handleKeyDown(e: React.KeyboardEvent) {
		if (e.key === "Escape") {
			setOpen(false);
		}
	}

	const hasValue = !!from || !!to;
	let displayText: string;
	if (from && to) {
		displayText = `${formatDisplay(from)} - ${formatDisplay(to)}`;
	} else if (from) {
		displayText = `${formatDisplay(from)} - ...`;
	} else {
		displayText = "Select date range";
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<div
				className={cn(
					"flex h-9 w-full items-center rounded-(--radius-sm) border border-(--color-border) bg-(--color-card) px-3 text-sm shadow-xs ring-offset-(--color-background) focus-within:ring-(--color-ring) focus-within:ring-2 focus-within:ring-offset-2",
					disabled && "cursor-not-allowed opacity-50",
					className,
				)}
				onKeyDown={handleKeyDown}
			>
				<PopoverTrigger asChild>
					<button
						ref={triggerRef}
						id={id}
						type="button"
						disabled={disabled}
						className={cn(
							"flex flex-1 items-center gap-2 bg-transparent text-left outline-none",
							!hasValue && "text-(--color-muted-foreground)",
						)}
					>
						<CalendarIcon className="size-4 shrink-0" />
						<span className="truncate">{displayText}</span>
					</button>
				</PopoverTrigger>
				{hasValue && !disabled && (
					<button
						type="button"
						onClick={handleClear}
						tabIndex={-1}
						className="ml-1 shrink-0 text-(--color-muted-foreground) hover:text-(--color-foreground)"
					>
						<XIcon className="size-3.5" />
					</button>
				)}
			</div>
			<PopoverContent
				className="w-auto p-0"
				onOpenAutoFocus={(e) => e.preventDefault()}
			>
				<Calendar
					mode="range"
					selected={range}
					onSelect={handleSelect}
					defaultMonth={from}
					numberOfMonths={2}
				/>
			</PopoverContent>
		</Popover>
	);
}
